<template>
  <div class="recruiting-search">
    <!-- Search Input -->
    <v-text-field
      v-model="search"
      class="search-input dark-input"
      prepend-icon="search"
      placeholder="Search contacts by name or phone"
      clearable
      clear-icon="cancel"
      :loading="loading"
      @input="debouncedSearch"
      @click:clear="clearSearch"
    />

    <!-- Results -->
    <div v-if="contacts.length" class="recruiting-search__results">
      <div
        v-for="contact in visibleContacts"
        :key="contact.con_id"
        class="recruiting-search__item"
        @click="openContact(contact)"
      >
        <div class="recruiting-search__info">
          <div class="recruiting-search__name">
            {{ getContactName(contact) }}
          </div>
          <div class="recruiting-search__phone">
            {{ contact.mobile || 'No phone' }}
          </div>
        </div>

        <div class="recruiting-search__actions">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                :disabled="!contact.mobile"
                v-on="on"
                color="appMain"
                @click.stop="makeCall(contact)"
              >
                <v-icon small>phone</v-icon>
              </v-btn>
            </template>
            <span>Call</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                :disabled="!contact.mobile"
                v-on="on"
                color="appMain"
                @click.stop="sendText(contact)"
              >
                <v-icon small>message</v-icon>
              </v-btn>
            </template>
            <span>Text</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                v-on="on"
                color="appMain"
                @click.stop="scheduleInterview(contact)"
              >
                <v-icon small>event</v-icon>
              </v-btn>
            </template>
            <span>Schedule Interview</span>
          </v-tooltip>
        </div>
      </div>

      <!-- Show More -->
      <v-btn
        v-if="hasMoreResults"
        x-small
        color="appMain"
        class="recruiting-search__more mt-2"
        @click="expanded = !expanded"
      >
        {{ expanded ? 'Show less' : `Show ${hiddenCount} more` }}
      </v-btn>
    </div>

    <!-- Empty State -->
    <div v-else-if="showEmptyState" class="recruiting-search__placeholder">
      No contacts found
    </div>

    <!-- Placeholder when no search -->
    <div v-else-if="!search && !loading" class="recruiting-search__placeholder">
      <div class="recruiting-search__placeholder-text">
        Search to call, text, or schedule
      </div>
    </div>

    <!-- Dialogs -->
    <recruiting-outreach-dialog
      single
      @openSnippetsPicker="onOpenSnippetsPicker"
      @openTextConversation="onSendCustomText"
      @makeCall="onMakeCall"
      @callAgain="onCallAgain"
    />
    <recruit-choose-snippet-dialog
      v-model="showSnippetsPicker"
      :contactId="activeContactId"
      @sendSMS="onSendSnippet"
    />
    <recruiting-appointment-dialog />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import EventBus from '@/factories/eventBus';
import { sendSMS } from '@/views/recruiting/recruitingHelpers';

import RecruitingOutreachDialog from '../dialogs/RecruitingOutreachDialog.vue';
import RecruitChooseSnippetDialog from '../dialogs/RecruitChooseSnippetDialog.vue';
import RecruitingAppointmentDialog from '../dialogs/RecruitingAppointmentDialog.vue';

export default {
  name: 'RecruitingHomeSearch',
  components: {
    RecruitingOutreachDialog,
    RecruitChooseSnippetDialog,
    RecruitingAppointmentDialog,
  },
  data() {
    return {
      search: '',
      contacts: [],
      loading: false,
      searched: false,
      searchDelay: null,
      showSnippetsPicker: false,
      activeContactId: null,
      expanded: false,
      visibleCount: 3,
    };
  },
  computed: {
    showEmptyState() {
      return (
        this.search.trim() &&
        !this.loading &&
        this.searched &&
        !this.contacts.length
      );
    },

    activeContact() {
      if (!this.activeContactId) return null;
      return this.contacts.find((c) => c.con_id === this.activeContactId);
    },

    visibleContacts() {
      if (this.expanded) return this.contacts;
      return this.contacts.slice(0, this.visibleCount);
    },

    hasMoreResults() {
      return this.contacts.length > this.visibleCount;
    },

    hiddenCount() {
      return this.contacts.length - this.visibleCount;
    },
  },
  methods: {
    ...mapActions(['loadFilteredContacts']),

    debouncedSearch() {
      clearTimeout(this.searchDelay);
      this.searchDelay = setTimeout(() => this.searchContacts(), 300);
    },

    async searchContacts() {
      const query = (this.search || '').trim();
      if (!query) {
        this.contacts = [];
        this.searched = false;
        return;
      }

      this.loading = true;
      this.expanded = false;
      try {
        const { results } = await this.loadFilteredContacts({
          filters: { name: query },
          limit: 20,
        });
        this.contacts = results || [];
        this.searched = true;
      } catch (err) {
        console.error('Failed to search contacts:', err);
        this.contacts = [];
      } finally {
        this.loading = false;
      }
    },

    clearSearch() {
      this.search = '';
      this.contacts = [];
      this.searched = false;
    },

    // Actions
    makeCall(contact) {
      if (!contact.mobile) return;
      this.activeContactId = contact.con_id;
      window.location.href = `tel:${contact.mobile}`;
      this.showOutreachDialog(contact, 'call');
    },

    sendText(contact) {
      if (!contact.mobile) return;
      this.activeContactId = contact.con_id;
      this.showSnippetsPicker = true;
    },

    scheduleInterview(contact) {
      this.activeContactId = contact.con_id;
      EventBus.$emit('showAppointmentDialog', { contactId: contact.con_id });
    },

    showOutreachDialog(contact, type, options = {}) {
      setTimeout(() => {
        EventBus.$emit('showOutreachDialog', {
          contactId: contact.con_id,
          type,
          ...options,
        });
      }, 1000);
    },

    // Dialog callbacks
    onOpenSnippetsPicker() {
      if (this.activeContact) {
        this.showSnippetsPicker = true;
      }
    },

    onSendCustomText() {
      if (this.activeContact?.mobile) {
        sendSMS(this.activeContact.mobile, '');
      }
    },

    onMakeCall() {
      if (this.activeContact?.mobile) {
        window.location.href = `tel:${this.activeContact.mobile}`;
      }
    },

    onCallAgain() {
      this.onMakeCall();
      if (this.activeContact) {
        this.showOutreachDialog(this.activeContact, 'call', {
          hideCallAgain: true,
        });
      }
    },

    onSendSnippet(message) {
      if (this.activeContact?.mobile) {
        sendSMS(this.activeContact.mobile, message);
        this.showOutreachDialog(this.activeContact, 'text');
      }
    },

    openContact(contact) {
      this.$router.push({
        name: 'contact-edit',
        params: { id: contact.con_id },
      });
    },

    getContactName(contact) {
      const { first_name, last_name, name } = contact;
      if (name) return name;
      return [first_name, last_name].filter(Boolean).join(' ') || 'Unknown';
    },
  },
};
</script>

<style lang="less">
.recruiting-search {
  padding: 12px;
  border-radius: @base-radius;
  background-color: #fff;
  box-shadow: @content-shadow;
  min-height: 116px;

  .search-input.v-input {
    .v-input__slot {
      padding: 4px 15px 4px 40px;
      box-shadow: none;
    }

    .v-input__prepend-outer {
      top: 5px;
      left: 8px;

      .v-icon {
        color: var(--v-appMain-base) !important;
      }
    }

    .v-input__append-inner {
      margin-top: 4px;
      margin-right: -5px;

      .v-icon {
        color: var(--v-appMain-base) !important;
      }
    }
  }

  &__results {
    margin-top: 8px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px;
    border-radius: @base-radius;
    transition: background-color 0.15s;
    cursor: pointer;
    background-color: var(--v-lightGray-base);

    &:hover {
      background-color: darken(#f5f5f5, 5%);
    }

    & + & {
      margin-top: 3px;
    }
  }

  &__info {
    min-width: 0;
    flex: 1;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    color: var(--v-appMain-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.3;
  }

  &__phone {
    font-size: 12px;
    color: #666;
    line-height: 1.3;
  }

  &__actions {
    display: flex;
    gap: 2px;
    margin-left: 8px;

    @media (min-width: 600px) {
      .v-btn .v-icon {
        font-size: 20px !important;
      }
    }
  }

  &__more {
    margin-top: 4px;
    margin-left: 8px;
  }

  &__placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 18px 12px 10px;
    text-align: center;
    gap: 8px;
  }

  &__placeholder-icons {
    display: flex;
    gap: 16px;

    .v-icon {
      opacity: 0.4;
    }
  }

  &__placeholder-text {
    color: #aaa;
    font-size: 13px;
  }

  // Mobile styles
  @media (max-width: 600px) {
    &__item {
      padding: 10px 12px;

      & + & {
        margin-top: 6px;
      }
    }

    &__name {
      font-size: 16px;
    }

    &__phone {
      font-size: 13px;
    }

    &__actions {
      gap: 4px;

      .v-btn {
        width: 40px;
        height: 40px;

        .v-icon {
          font-size: 22px !important;
        }
      }
    }
  }
}
</style>
